import React, { useEffect, useState } from "react";

import { MenuItem, SelectChangeEvent } from "@mui/material";
import { Tabs } from "@mui/base/Tabs";
import { Grid, IconButton, Menu } from "@mui/material";
import Stack from "@mui/material/Stack";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { FiActivity } from "react-icons/fi";
import { IoEllipsisVertical } from "react-icons/io5";

import { Status } from "../../../util/enums";
import Button, { Color } from "../../Form/Button";
import Select from "../../Form/Select";
import Modal, { StatusFieldProps } from "../../Generic/Modal";
import { UITabFooter } from "../UITabFooter";
import useStyles, { Badge, Tab, TabPanel, TabsList } from "./styles";

interface PanelProps {
  menuItem?: string;
  "data-cy"?: string;
  key?: string;
  render?: () => React.ReactNode;
  loading?: boolean;
  skeleton?: () => React.ReactNode;
  badgeValue?: string | number;
  disabled?: boolean;
  className?: string;
  icon?: React.ReactChild;
  iconAction?: Function;
  tabClassName?: string;
  hasError?: boolean;
  badgeClassName?: string;
  skipContainerPadding?: boolean;
  flexContainer?: boolean;
  rounded?: boolean;
}

interface TabProps {
  leftPanels: PanelProps[];
  showRightPanel?: boolean;
  rightPanels?: PanelProps[];
  showActivityButton?: boolean;
  activityPanels?: PanelProps[];
  rightComponent?: () => React.ReactNode;
  selectedLeftPanel?: number;
  selectedRightPanel?: number;
  selectedActivityPanel?: number;
  onChangeLeftPanel?: (
    event: React.SyntheticEvent<Element, Event>,
    panel: number,
    key?: string
  ) => void;
  onChangeRightPanel?: (
    event: React.SyntheticEvent<Element, Event>,
    panel: number,
    key?: string
  ) => void;
  onChangeActivityPanel?: (
    event: React.SyntheticEvent<Element, Event>,
    panel: number,
    key?: string
  ) => void;
  className?: string;
  gridClassName?: string;
  leftPanelClassName?: string;
  leftDataCy?: string;
  rightDataCy?: string;
  activityDataCy?: string;
  isRenderingStatusField?: boolean;
  statusFieldProps?: StatusFieldProps;
  isRenderMoreOptionsButton?: boolean;
  onSaveAndSend?: () => void;
  isSendingToCustomer?: boolean;
  showActivityModal?: boolean;
  tabFooter?: JSX.Element;
  tabBackgroundColor?: string;
  currentActivePanelRef?: (element: HTMLElement | null) => void;
  onlyRenderTheCurrentActivePanel?: boolean;
  useInitialPadding?: boolean;
  rightComponentClasses?: string;
}

/**
 *
 * @property leftPanels: Array of PanelProps with left-side components to render (in order by index ascending)
 * @property showRightPanel: Boolean value that displays the Right Grid
 * @property rightPanels: Optional Array of PanelProps with right-side components ro render (in order by index ascending)
 * @property selectedLeftPanel: Index Number of the left selected Panel to be rendered
 * @property selectedRightPanel: Optional Index Number of the right selected Panel ro be rendered
 * @property selectedActivityPanel: Index Number of the activity modal selected Panel to be rendered
 * @property showActivityButton: Boolean value that displays the View activity button for the modal
 * @property activityPanels: Optional Array of PanelProps with activity modal components ro render (in order by index ascending)
 * @property rightComponent: Optional Component that displays aside show activity button
 * @property onChangeLeftPanel: Callback Function that returns the selected left panel value
 * @property onChangeRightPanel: Callback Function that returns the selected right panel value
 * @property onChangeActivityPanel: Callback Function that returns the selected activity modal panel value
 * @property className: Optional String of classnames for external style in tab
 * @property leftDataCy: Optional unique String for identify the left panel in tests with Cypress
 * @property rightDataCy: Optional unique String for identify the right panel in tests with Cypress
 * @property activityDataCy: Optional unique String for identify the activity modal panel in tests with Cypress
 * @property tabFooter: Optional JSX Element to render tab footer
 * @example Usage
 * <UITabMaterial
      leftPanels={[...leftPanels]}
      showRightPanel
      rightPanels={[...rightPanels]}
      showActivityButton
      activityPanels={[...activityPanels]}
      rightComponent={<Input />}
      selectedLeftPanel={0}
      selectedRightPanel={0}
      selectedActivityPanel={0}
      setSelectedLeftPanel={(event, value) => {}}
      setSelectedRightPanel={(event, value) => {}}
      setSelectedActivityPanel={(event, value) => {}}
      className="any-classnames"
      leftDataCy="any-unique-string-1"
      rightDataCy="any-unique-string-2"
      activityDataCy="any-unique-string-3"
      tabFooter={<SomeFooterElement/>}
    />
 * @returns JSX Element rendering a Grid with two Tabs componenent
 */
const UITabMaterial = ({
  leftPanels,
  showRightPanel,
  showActivityButton,
  activityPanels,
  rightComponent,
  rightPanels,
  selectedLeftPanel,
  selectedRightPanel,
  selectedActivityPanel,
  onChangeLeftPanel,
  onChangeRightPanel,
  onChangeActivityPanel,
  className,
  gridClassName,
  leftPanelClassName,
  leftDataCy,
  rightDataCy,
  activityDataCy,
  isRenderingStatusField,
  statusFieldProps,
  isRenderMoreOptionsButton = false,
  onlyRenderTheCurrentActivePanel,
  onSaveAndSend,
  showActivityModal = false,
  tabFooter,
  tabBackgroundColor,
  currentActivePanelRef,
  useInitialPadding,
  rightComponentClasses,
}: TabProps) => {
  const classes = useStyles();
  const { t: translate } = useTranslation();

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [selectedLeftPanelValue, setSelectedLeftPanelValue] = useState(0);
  const [selectedRightPanelValue, setSelectedRightPanelValue] = useState(0);
  const [selectedActivityPanelValue, setSelectedActivityPanelValue] = useState(0);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  useEffect(() => {
    if (selectedLeftPanel !== undefined) {
      const nextSelectedPanel = selectedLeftPanel < 0 ? 0 : selectedLeftPanel
      if (selectedLeftPanelValue !== nextSelectedPanel) setSelectedLeftPanelValue(nextSelectedPanel);
    }
  }, [selectedLeftPanel, leftPanels?.length]);


  useEffect(() => {
    if (selectedRightPanel !== undefined) setSelectedRightPanelValue(selectedRightPanel);
  }, [selectedRightPanel]);

  useEffect(() => {
    if (selectedActivityPanel !== undefined) setSelectedActivityPanelValue(selectedActivityPanel);
  }, [selectedActivityPanel]);

  const isRightPanelValid = rightPanels && showRightPanel && rightPanels?.length > 0;

  const isActivityPanelValid = activityPanels && showActivityButton && activityPanels.length > 0;

  const handleLeftTabChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: string | number,
    key?: string
  ) => {
    setSelectedLeftPanelValue(Number(value));
    onChangeLeftPanel?.(event, Number(value), key);
  };

  const handleRightTabChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: string | number,
    key?: string
  ) => {
    setSelectedRightPanelValue(Number(value));
    if (onChangeRightPanel) onChangeRightPanel(event, Number(value), key);
  };

  const handleActivityTabChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: string | number,
    key?: string
  ) => {
    setSelectedActivityPanelValue(Number(value));
    if (onChangeActivityPanel) onChangeActivityPanel(event, Number(value), key);
  };

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const renderTabContent = (
    {
      render,
      className,
      loading = false,
      skeleton,
      key,
      skipContainerPadding,
      flexContainer,
      rounded,
    }: PanelProps,
    isCurrentActiveTab: boolean,
    index: number,
    isActivityPanel?: boolean
  ) => {
    if (loading) {
      return (
        <TabPanel
          key={`${key}-loading`}
          className={`${classes.tabPanel} ${className ?? ""}`}
          value={index}
          backgroundColor={tabBackgroundColor}
          skipContainerPadding={skipContainerPadding}
          flexContainer={flexContainer}
          rounded={rounded}
        >
          {skeleton?.()}
        </TabPanel>
      );
    }

    const renderContent = () => {
      if (onlyRenderTheCurrentActivePanel) {
        if (isCurrentActiveTab) return render?.();

        return;
      }

      return render?.();
    };

    return (
      <TabPanel
        style={{
          height: isActivityPanel ? "calc(100vh - 330px)" : "",
        }}
        className={`${classes.tabPanel} ${className ?? ""} ${key}-panel`}
        value={index}
        key={`${key}-panel`}
        backgroundColor={tabBackgroundColor}
        skipContainerPadding={skipContainerPadding}
        flexContainer={flexContainer}
        rounded={rounded}
        ref={(value: HTMLElement | null) => {
          if (!value || !isCurrentActiveTab) return;
          currentActivePanelRef?.(value);
        }}
      >
        {renderContent()}
      </TabPanel>
    );
  };

  const renderStatusField = (statusFieldProps?: StatusFieldProps) => {
    return (
      statusFieldProps && (
        <div className={classes.statusWrapper}>
          <Select
            className={classes.statusField}
            name="statusField"
            displayEmpty
            data-cy={statusFieldProps?.dataCy}
            onChange={(event: SelectChangeEvent<any>) =>
              statusFieldProps?.handleChange(event.target.value)
            }
            {...statusFieldProps}
          >
            <MenuItem value={Status.ACTIVE} data-cy={Status.ACTIVE}>
              {translate("active")}
            </MenuItem>
            <MenuItem value={Status.INACTIVE} data-cy={Status.INACTIVE}>
              {translate("inactive")}
            </MenuItem>
          </Select>
        </div>
      )
    );
  };

  const renderRightComponent = (component: () => React.ReactNode, classes?: string) => {
    return <div className={classes}>{component()}</div>;
  };

  const renderActivityButton = (setIsModalOpen?: React.Dispatch<React.SetStateAction<boolean>>) => {
    return (
      <Button
        onClick={() => {
          if (setIsModalOpen) setIsModalOpen((isModalOpen) => !isModalOpen);
        }}
        className={classes.activityButton}
        customColor={Color.SECONDARY}
      >
        <Stack alignItems="center" justifyContent="center" width="100%" direction="row" spacing={1}>
          <FiActivity color="#FFF" />
          <span>{translate("viewActivity")}</span>
        </Stack>
      </Button>
    );
  };

  const renderMoreOptionsButton = () => {
    return (
      <>
        <IconButton className={classes.iconMoreOptions} onClick={handleOpenMenu}>
          <IoEllipsisVertical color="#0055A4" />
        </IconButton>
        <Menu
          className={classes.menu}
          anchorEl={anchorEl}
          keepMounted
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
          transformOrigin={{ horizontal: "right", vertical: "top" }}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          PaperProps={{
            className: classes.menuContainer,
          }}
        >
          <MenuItem className={classes.menuItemSave} onClick={onSaveAndSend}>
            {translate("saveAndSendToCustomer")}
          </MenuItem>
          <MenuItem
            onClick={() => {
              if (setIsModalOpen) {
                setIsModalOpen((isModalOpen) => !isModalOpen);
                setAnchorEl(null);
              }
            }}
          >
            {translate("viewActivity")}
          </MenuItem>
        </Menu>
      </>
    );
  };

  const renderTab = (panelProps: PanelProps, index: number) => {
    if (panelProps?.badgeValue) {
      return (
        <Tab
          value={index}
          data-cy={panelProps["data-cy"] || panelProps?.key}
          key={panelProps?.key}
          disabled={panelProps?.disabled || false}
        >
          {String(panelProps?.menuItem)}
          <Badge className={panelProps?.badgeClassName}>{panelProps?.badgeValue}</Badge>
        </Tab>
      );
    }

    if (panelProps?.icon) {
      return (
        <Tab
          value={index}
          data-cy={panelProps["data-cy"] || panelProps?.key}
          key={panelProps?.key}
          disabled={panelProps?.disabled || false}
          onClick={() => panelProps?.iconAction?.()}
          className={`${panelProps?.tabClassName || ""} ${
            panelProps?.hasError ? classes.tabError : ""
          }`}
        >
          {panelProps?.icon}
        </Tab>
      );
    }

    return (
      <Tab
        data-cy={panelProps["data-cy"] || panelProps?.key}
        value={index}
        key={panelProps?.key}
        disabled={panelProps?.disabled || false}
        className={`${panelProps?.tabClassName || ""} ${
          panelProps?.hasError ? classes.tabError : ""
        }`}
      >
        {String(panelProps?.menuItem)}
      </Tab>
    );
  };

  const renderTabs = (
    selectedTab: number,
    setSelectedTab: (
      event: React.SyntheticEvent<Element, Event>,
      index: string | number,
      key?: string
    ) => void,
    panels: PanelProps[],
    className?: string,
    dataCy?: string,
    isActivityPanel?: boolean,
    isActivityPanelValid?: boolean,
    rightComponent?: () => React.ReactNode,
    rightComponentClasses?: string,
    setIsModalOpen?: React.Dispatch<React.SetStateAction<boolean>>,
    isRenderingStatusField?: boolean,
    statusFieldProps?: StatusFieldProps,
    isRenderingMoreOptions?: boolean
  ) => {
    return (
      <Tabs
        className={className}
        data-cy={dataCy}
        onChange={(event, value) => {
          if (panels?.length > 0 && panels[Number(value)]) {
            setSelectedTab(
              event!,
              Number(value),
              panels[Number(value)]?.key || panels[Number(value)]?.menuItem?.toLowerCase()
            );
          }
        }}
        defaultValue={0}
        orientation="horizontal"
        value={selectedTab}
      >
        <TabsList className={classes.tabList} useInitialPadding={useInitialPadding}>
          {panels.map((panel: PanelProps, index: number) => renderTab(panel, index))}
          <Grid container direction="row" justifyContent="flex-end">
            {rightComponent && renderRightComponent(rightComponent, rightComponentClasses)}
            {isActivityPanelValid && renderActivityButton(setIsModalOpen)}
            {isRenderingStatusField && renderStatusField(statusFieldProps)}
            {isRenderingMoreOptions && renderMoreOptionsButton()}
          </Grid>
        </TabsList>
        {panels.map((panel: PanelProps, index: number) =>
          renderTabContent(panel, selectedTab === index, index, isActivityPanel)
        )}
      </Tabs>
    );
  };

  return (
    <>
      <Grid className={gridClassName} container direction="row" spacing={2}>
        <Grid
          item
          xs={12}
          md={isRightPanelValid && showRightPanel ? 8 : 12}
          className={
            tabFooter
              ? classNames(classes.removePaddingBottom, leftPanelClassName)
              : leftPanelClassName
          }
        >
          {renderTabs(
            selectedLeftPanelValue,
            handleLeftTabChange,
            leftPanels,
            className,
            leftDataCy,
            false,
            isActivityPanelValid,
            rightComponent,
            rightComponentClasses,
            setIsModalOpen,
            isRenderingStatusField,
            statusFieldProps,
            isRenderMoreOptionsButton
          )}
        </Grid>
        {tabFooter && (
          <Grid item xs={12} className={classes.actionsButtons}>
            <UITabFooter>{tabFooter}</UITabFooter>
          </Grid>
        )}
        {isRightPanelValid && (
          <Grid item xs={12} md={4}>
            {renderTabs(
              selectedRightPanelValue,
              handleRightTabChange,
              rightPanels,
              className,
              rightDataCy,
              false,
              false
            )}
          </Grid>
        )}
      </Grid>
      {(isActivityPanelValid || (showActivityModal && activityPanels?.length)) && (
        <Modal
          open={isModalOpen}
          title={translate("activity")}
          handleClose={() => setIsModalOpen(false)}
          showActionButtons
          actionButtonProps={{
            saveDisabled: true,
            textCancel: translate("close"),
            customColorCancel: Color.SECONDARY,
            customColorSave: Color.NOT_SELECTED,
          }}
        >
          {renderTabs(
            selectedActivityPanelValue,
            handleActivityTabChange,
            activityPanels,
            classes.modalTabs,
            activityDataCy,
            true,
            false
          )}
        </Modal>
      )}
    </>
  );
};

export { PanelProps };
export default UITabMaterial;
